/**
 * useStudentTutorConversation — la conversation avec un tuteur, prête à poser : les props de
 * `<StudentTutorConversationPage>` et celles de ses fenêtres (`StudentTutorConversationOverlays`).
 *
 * Crochet de composition : les lectures (`useStudentTutorConversationData`), les gestes
 * (`useStudentTutorConversationState`), le lecteur des vocaux (`useConversationVoicePlayer`), la
 * file d'envoi des vocaux (`messengerVoiceOutbox`, partagée par toutes les conversations montées) et
 * le view-model. Deux lecteurs : la route `messenger` (`StudentTutorConversationScreen`) et le volet
 * droit de « Messages » au large (`StudentTutoringTwoPane`).
 *
 * Un vocal envoyé d'ici entre dans la boîte d'envoi du fil comme les autres envois (« Envoi... »,
 * puis « Non envoyé » et « Renvoyer ») ; c'est la file qui l'envoie, le garde si l'app est tuée et le
 * renvoie.
 *
 * Sur le web, comme l'ancien fil : le champ est prêt à l'ouverture, et remonter le fil charge seul
 * les messages précédents.
 */
import { useCallback, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"

import { API_URL } from "../../../../../../data/constants/apiConstants"
import { ownNetworkFailures } from "../../../../../../data/utility/networkFailureOwner"
import { type StudentTutorConversationPageProps } from "../../../../../../design-system/student"
import { useVoiceOutbox, type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { tutorConversationTarget } from "../../../../adapters/student/Messaging/studentConversationAdapter"
import {
    makeStudentTutorConversationViewModel,
    type StudentTutorConversationViewModel,
} from "../../../../view-models/student/Messaging/studentTutorConversationViewModel"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { mergeOutboxes, voiceOutboxEntries } from "./conversationVoiceOutbox"
import { messengerVoiceFileName, messengerVoiceOutbox } from "./messengerVoiceOutbox"
import { type StudentTutorConversationOverlaysProps } from "./StudentTutorConversationOverlays"
import { CONVERSATION_ATTACH_SOURCES, useStudentTutorConversationState } from "./useStudentTutorConversationState"
import { useConversationVoicePlayer } from "./useConversationVoicePlayer"
import { useStudentTutorConversationData } from "./useStudentTutorConversationData"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

export type UseStudentTutorConversationArgs = {
    /** `null` : le tuteur principal (la route sans paramètre). */
    tutorId: string | number | null
    /** Dans le volet de « Messages » : voir `useStudentTutorConversationState`. */
    embedded: boolean
    /** Le retour ; absent dans un volet au large. */
    onBack?: () => void
    /**
     * `false` : la conversation n'est pas à l'écran (le volet de « Messages », rien d'ouvert ou
     * sur un téléphone) — aucun fil n'est lu ni marqué lu. Vrai par défaut.
     */
    active?: boolean
}

/** Ce que rend une conversation cachée : sa page n'est pas posée. */
const INACTIVE_VIEW_MODEL: StudentTutorConversationViewModel = {
    tutor: { id: 0, firstname: "", lastname: "" },
    threadId: null,
    days: [],
    messages: [],
    loading: false,
    error: null,
    hasEarlier: false,
}

export function useStudentTutorConversation(args: UseStudentTutorConversationArgs): {
    page: StudentTutorConversationPageProps
    overlays: StudentTutorConversationOverlaysProps
} {
    const { tutorId, embedded, onBack, active = true } = args
    // Le compte, pas le fil : la recherche du fil ne se refait qu'à un changement du compte, pas à
    // chaque action Redux ; cachée, la conversation ne suit pas le compte.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const user = useSelector((state: any) => (active ? selectDisplayedStudent(state) : null))
    const threadId = useMemo(
        () => (active ? tutorConversationTarget(user, tutorId).threadId : null),
        [active, user, tutorId]
    )
    // À l'écran, la conversation dit elle-même ses échecs réseau (« Non envoyé » et « Renvoyer », le
    // fil qui ne se lit pas) : les boîtes legacy ne s'ouvrent pas par-dessus.
    useEffect(() => (active ? ownNetworkFailures() : undefined), [active])
    const data = useStudentTutorConversationData(threadId)
    const { state, actions, sending, attaching } = useStudentTutorConversationState({ threadId, embedded })
    const voice = useConversationVoicePlayer(threadId)

    // Les vocaux de la file, pour ce fil, rejoignent la boîte d'envoi de l'écran.
    const voiceQueue = useVoiceOutbox(messengerVoiceOutbox)
    const voiceEntries = useMemo(() => voiceOutboxEntries(voiceQueue.items, threadId), [voiceQueue.items, threadId])
    const outbox = useMemo(() => mergeOutboxes(state.outbox, voiceEntries), [state.outbox, voiceEntries])
    const displayName = (user as { display_name?: string } | null)?.display_name

    const sendVoice = useCallback(
        (file: VoiceFile) => {
            if (threadId == null) return
            messengerVoiceOutbox.enqueue(file, {
                threadId,
                fileName: messengerVoiceFileName(displayName, file.extension),
            })
        },
        [threadId, displayName]
    )
    const retryMessage = useCallback(
        (message: UIConversationMessage) => {
            if (voiceQueue.items.some((item) => item.id === message.id)) messengerVoiceOutbox.retry(message.id)
            else actions.retry(message)
        },
        [voiceQueue.items, actions]
    )
    const attach = useCallback(
        (source: Parameters<typeof actions.attach>[0]) => {
            // L'enregistreur s'ouvre : rien ne continue de jouer par-dessus.
            if (source === "voice") voice.stop()
            actions.attach(source)
        },
        [actions, voice]
    )

    // Le sien : deux conversations peuvent être montées à la fois (voir la fabrique). Cachée (le
    // volet de « Messages » sur un téléphone, ou rien d'ouvert), elle ne regroupe pas le fil.
    const viewModel = useMemo(makeStudentTutorConversationViewModel, [])
    const vm = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) =>
            !active
                ? INACTIVE_VIEW_MODEL
                : viewModel(s, {
                      tutorId,
                      apiUrl: API_URL as string,
                      outbox,
                      error: data.error,
                      hasEarlier: data.hasEarlier,
                      voiceMessageId: voice.messageId,
                      voiceLabel: voice.label,
                  })
    )
    const canWrite = vm.threadId != null

    return {
        page: {
            tutor: vm.tutor,
            caption: vm.caption,
            onBack,
            days: vm.days,
            loading: vm.loading,
            error: vm.error ?? undefined,
            onRetry: vm.threadId != null ? data.retry : undefined,
            hasEarlier: vm.hasEarlier,
            loadingEarlier: data.loadingEarlier,
            onLoadEarlier: data.loadEarlier,
            autoLoadEarlier: IS_WEB,
            onRetryMessage: retryMessage,
            onOpenAttachment: (message) => actions.openAttachment(message, vm.messages),
            onMessageLongPress: actions.openCopy,
            voicePlayback: voice.playback,
            onToggleVoice: voice.toggle,
            onSeekVoice: voice.seek,
            onCycleVoiceRate: voice.cycleRate,
            onPressLink: actions.pressLink,
            draft: state.draft,
            onChangeDraft: actions.changeDraft,
            onSend: actions.send,
            sending,
            attachSources: canWrite ? CONVERSATION_ATTACH_SOURCES : [],
            onAttach: attach,
            attaching,
            autoFocus: IS_WEB,
        },
        overlays: {
            copyOpen: state.copyMessage != null,
            onCopy: actions.copy,
            onCloseCopy: actions.closeCopy,
            viewer: state.viewer,
            onCloseViewer: actions.closeViewer,
            unsentNotice: state.unsentNotice,
            onHideUnsent: actions.hideUnsent,
            phone: state.phone,
            onChoosePhone: actions.choosePhone,
            onClosePhone: actions.closePhone,
            recorder:
                state.recorderOpen && canWrite
                    ? { threadId: vm.threadId, onClose: actions.closeRecorder, onSend: sendVoice }
                    : null,
        },
    }
}
